import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { message, Modal } from "antd";
import ImportData from "../../component/ModalComponent/ModalComponent";
import { OlympicResult } from "../../types/OlympicType";
import { fetchAll } from "../../services/AdminService";
import HeaderComponent from "../../component/HeaderComponent/HeaderComponent";
import "./style.css";
import axiosInstance from "../../services/axiosConfig";
import StatusModal from "../../component/StatusModal";

const AdminPage: React.FC = () => {
  const [adminQuery, setAdminQuery] = useState({
    studentCode: "",
    fullName: "",
    birthday: "",
    status: "",
    monThi: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasAdminSearched, setHasAdminSearched] = useState(false);
  const [adminOlympicResults, setAdminOlympicResults] = useState<
    OlympicResult[]
  >([]);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<OlympicResult | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAllRecords = async (page: number = 0) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchAll(
        page,
        {
          studentCode: adminQuery.studentCode,
          fullName: adminQuery.fullName,
          birthday: adminQuery.birthday,
          status:
            adminQuery.status !== "" ? parseInt(adminQuery.status) : undefined,
          monThi: adminQuery.monThi,
        },
        pageSize
      );

      if (data?.content) {
        setAdminOlympicResults(data.content);
        setTotalPages(data.totalPages);
        setCurrentPage(data.number);
        setTotalRecords(data.totalElements);
        setHasAdminSearched(true);
      } else {
        setAdminOlympicResults([]);
      }
    } catch (error) {
      console.error("Error fetching records:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Có lỗi xảy ra khi tải dữ liệu.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllRecords();
  }, [pageSize]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchAllRecords(newPage);
  };

  const handleAdminSearch = async () => {
    setCurrentPage(0);
    await fetchAllRecords(0);
  };

  const Pagination = () => {
    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 0}
        >
          Trang trước
        </button>

        <span>
          Trang {currentPage + 1} / {totalPages}
        </span>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
        >
          Trang sau
        </button>

        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(0);
          }}
        >
          <option value={10}>10 / trang</option>
          <option value={20}>20 / trang</option>
          <option value={50}>50 / trang</option>
        </select>
        <span>Tổng số bản ghi: {totalRecords}</span>
      </div>
    );
  };

  const renderAdminResults = () => {
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelectedItems(adminOlympicResults.map((item) => Number(item.id)));
      } else {
        setSelectedItems([]);
      }
    };

    const handleDelete = async () => {
      confirm({
        title: "Bạn có chắc chắn muốn xóa các bản ghi đã chọn?",
        content:
          "Hành động này sẽ không thể hoàn tác. Bạn có chắc muốn tiếp tục?",
        okText: "Có",
        okType: "danger",
        cancelText: "Không",
        onOk: async () => {
          setIsDeleting(true);
          try {
            const response = await axiosInstance.post(
              `/olympic_uet/deleteAll`,
              {
                listOlympicId: selectedItems,
              }
            );

            if (response.status === 200) {
              message.success("Xóa thành công!");
              setSelectedItems([]);
              await fetchAllRecords();
            }
          } catch (error) {
            console.error("Error deleting items:", error);
            if (axios.isAxiosError(error)) {
              message.error(
                error.response?.data?.message || "Có lỗi xảy ra khi xóa!"
              );
            } else {
              message.error("Có lỗi xảy ra khi xóa!");
            }
          } finally {
            setIsDeleting(false);
          }
        },
        onCancel() {
          console.log("Hủy hành động xóa");
        },
      });
    };

    const handleSelectItem = (id: number) => {
      setSelectedItems((prev) => {
        if (prev.includes(id)) {
          return prev.filter((item) => item !== id);
        } else {
          return [...prev, id];
        }
      });
    };

    const { confirm } = Modal;

    if (!hasAdminSearched) return null;
    if (!adminOlympicResults.length) return <p>Không có dữ liệu.</p>;
    return (
      <>
        {selectedItems.length > 0 && (
          <div className="bulk-actions">
            <button
              className={`delete-btn ${isDeleting ? "deleting" : ""}`}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner"></span>
                  Đang xóa...
                </>
              ) : (
                <>Xóa ({selectedItems.length}) bản ghi</>
              )}
            </button>
          </div>
        )}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: "50px" }} className="checkbox-cell">
                  <input
                    className="checkbox"
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={
                      selectedItems.length === adminOlympicResults.length
                    }
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedItems.length > 0 &&
                          selectedItems.length < adminOlympicResults.length;
                      }
                    }}
                  />
                </th>
                <th style={{ width: "50px" }}>STT</th>
                <th style={{ width: "100px" }}>Số báo danh</th>
                <th style={{ width: "150px" }}>Họ và tên</th>
                <th style={{ width: "100px" }}>Ngày sinh</th>
                <th style={{ width: "120px" }}>Nơi sinh</th>
                <th style={{ width: "150px" }}>Trường THPT</th>
                <th style={{ width: "100px" }}>Môn thi</th>
                <th style={{ width: "100px" }}>Thời gian thi</th>
                <th style={{ width: "100px" }}>Phòng thi</th>
                <th style={{ width: "100px" }}>Địa điểm</th>
                <th style={{ width: "150px" }}>Trạng thái</th>
                <th style={{ width: "150px" }}>Lý do</th>
                <th style={{ width: "100px" }}>Điểm</th>
                <th style={{ width: "100px" }}>Kết quả</th>
                <th style={{ width: "100px" }}>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {adminOlympicResults.map((result, index) => (
                <tr
                  key={result.id}
                  className={
                    selectedItems.includes(Number(result.id))
                      ? "selected-row"
                      : ""
                  }
                >
                  <td style={{ userSelect: "none" }} className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(Number(result.id))}
                      onChange={() => handleSelectItem(Number(result.id))}
                    />
                  </td>
                  <td style={{ userSelect: "none" }}>
                    {currentPage * pageSize + (index + 1)}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.studentCode || ""}
                        onChange={(e) => handleChange("sbd", e.target.value)}
                      />
                    ) : (
                      result.studentCode
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.fullName || ""}
                        onChange={(e) =>
                          handleChange("fullName", e.target.value)
                        }
                      />
                    ) : (
                      result.fullName
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.birthday || ""}
                        onChange={(e) =>
                          handleChange("birthday", e.target.value)
                        }
                      />
                    ) : (
                      result.birthday
                    )}
                  </td>

                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.place || ""}
                        onChange={(e) => handleChange("place", e.target.value)}
                      />
                    ) : (
                      result.place
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.highSchool || ""}
                        onChange={(e) =>
                          handleChange("highSchool", e.target.value)
                        }
                      />
                    ) : (
                      result.highSchool
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.monThi || ""}
                        onChange={(e) => handleChange("monThi", e.target.value)}
                      />
                    ) : (
                      result.monThi
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.timeExam || ""}
                        onChange={(e) =>
                          handleChange("timeExam", e.target.value)
                        }
                      />
                    ) : (
                      result.timeExam
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.roomExam || ""}
                        onChange={(e) =>
                          handleChange("roomExam", e.target.value)
                        }
                      />
                    ) : (
                      result.roomExam
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.locationExam || ""}
                        onChange={(e) =>
                          handleChange("locationExam", e.target.value)
                        }
                      />
                    ) : (
                      result.locationExam
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <select
                        value={editedData?.status || 0}
                        onChange={(e) =>
                          handleChange("status", parseInt(e.target.value))
                        }
                      >
                        <option value={1}>Đủ điều kiện dự thi</option>
                        <option value={0}>Không đủ điều kiện</option>
                      </select>
                    ) : (
                      <span
                        className={`status-badge ${
                          result.status === 1 ? "active" : "inactive"
                        }`}
                      >
                        {result.status === 1
                          ? "Đủ điều kiện dự thi"
                          : "Không đủ điều kiện dự thi"}
                      </span>
                    )}
                  </td>

                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.reason || ""}
                        onChange={(e) => handleChange("reason", e.target.value)}
                      />
                    ) : (
                      result.reason
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={
                          editedData?.point !== undefined
                            ? editedData.point
                            : ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChange(
                            "point",
                            value === "" ? undefined : parseFloat(value)
                          );
                        }}
                      />
                    ) : (
                      result.point
                    )}
                  </td>
                  <td>
                    {editingRow === result.id ? (
                      <input
                        type="text"
                        value={editedData?.result || ""}
                        onChange={(e) => handleChange("result", e.target.value)}
                      />
                    ) : (
                      result.result
                    )}
                  </td>
                  <td style={{ display: "flex" }}>
                    {editingRow === result.id ? (
                      <>
                        <button
                          className="save-btn"
                          onClick={() => updateChange(result)}
                        >
                          Lưu
                        </button>
                        <button className="unsave-btn" onClick={cancelEditing}>
                          Hủy
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="edit-btn"
                          onClick={() => startEditing(result)}
                        >
                          Sửa
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination />
      </>
    );
  };

  const startEditing = (result: OlympicResult) => {
    setEditingRow(result.id);
    setEditedData({ ...result });
  };

  // Hàm hủy chỉnh sửa
  const cancelEditing = () => {
    setEditingRow(null);
    setEditedData(null);
  };

  const handleChange = (field: keyof OlympicResult, value: any) => {
    if (editedData) {
      setEditedData({
        ...editedData,
        [field]: value,
      });
    }
  };

  const updateChange = async (result: OlympicResult) => {
    if (!editedData) return;

    try {
      await axiosInstance.post(
        `/olympic_uet/update/${editedData.id}`,
        editedData
      );

      const updatedResults = adminOlympicResults.map((item) =>
        item.id === editedData.id ? editedData : item
      );
      setAdminOlympicResults(updatedResults);

      setEditingRow(null);
      setEditedData(null);
    } catch (error) {
      console.error("Error updating record:", error);
      setError("Có lỗi xảy ra khi cập nhật dữ liệu.");
    }
  };

  const formatDate = (value: string) => {
    // Loại bỏ các ký tự không phải số
    value = value.replace(/\D/g, "");

    // Lấy ngày, tháng và năm từ chuỗi
    let day = value.slice(0, 2);
    let month = value.slice(2, 4);
    let year = value.slice(4, 8);

    // Kết hợp ngày, tháng và năm với dấu "/"
    let formattedDate = [day, month, year].filter(Boolean).join("/");

    return formattedDate;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatDate(e.target.value);
    setAdminQuery((prevQuery) => ({
      ...prevQuery,
      birthday: formattedValue, // Cập nhật giá trị ngày sinh sau khi định dạng
    }));
    setError(null);
  };

  return (
    <>
      <HeaderComponent />
      <div className="admin-interface">
        <div className="search">
          {/* Tìm kiếm họ tên */}
          <input
            type="text"
            placeholder={error ? error : "Nhập họ tên sinh viên"}
            value={adminQuery.fullName}
            onChange={(e) => {
              setAdminQuery({ ...adminQuery, fullName: e.target.value });
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAdminSearch();
            }}
            className={error ? "error-input" : ""}
          />
          <input
            type="text"
            placeholder={error ? error : "Nhập số báo danh"}
            value={adminQuery.studentCode}
            onChange={(e) => {
              setAdminQuery({ ...adminQuery, studentCode: e.target.value });
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAdminSearch();
            }}
            className={error ? "error-input" : ""}
          />

          {/* Tìm kiếm theo ngày sinh */}
          <input
            type="text"
            onChange={handleDateChange}
            placeholder="Ngày sinh (dd/mm/yyyy)"
            value={adminQuery.birthday}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAdminSearch();
            }}
          />

          {/* Tìm kiếm theo trạng thái */}

          <input
            type="text"
            placeholder={error ? error : "Nhập môn thi"}
            value={adminQuery.monThi}
            onChange={(e) => {
              setAdminQuery({ ...adminQuery, monThi: e.target.value });
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAdminSearch();
            }}
            className={error ? "error-input" : ""}
          />

          <select
            value={
              adminQuery.status !== undefined && adminQuery.status !== ""
                ? adminQuery.status
                : ""
            }
            onChange={(e) => {
              const value = e.target.value !== "" ? Number(e.target.value) : "";
              setAdminQuery({
                ...adminQuery,
                status: value !== "" ? String(value) : "", // Luôn giữ giá trị 0 và chuyển thành chuỗi chỉ khi giá trị khác rỗng
              });
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleAdminSearch();
            }}
          >
            <option value="">Trạng thái</option>
            <option value={1}>Đủ điều kiện dự thi</option>
            <option value={0}>Không đủ điều kiện</option>
          </select>

          {/* Nút tìm kiếm */}
          <button onClick={handleAdminSearch}>Tìm kiếm</button>
        </div>
        <div className="file-upload-container">
          <div className="file-upload">
            <ImportData />
          </div>
        </div>

        {renderAdminResults()}
      </div>
    </>
  );
};

export default AdminPage;
